import type { Availability, Persisted, Poll, PollResponse } from '@tunasong/schemas'
import { isDateSlot } from '@tunasong/schemas'
import { getLiveSlotIndices, isPastSlot } from './date-slot.js'

export interface SlotTally {
  yes: number
  maybe: number
  no: number
}

/** Which option indices a single response has an answer for. A `false` counts: it is a deliberate no. */
const getAnsweredIndices = (response: PollResponse) => {
  const indices = Object.keys(response.availability ?? response.choices ?? {}).map(Number)
  return typeof response.choice === 'number' ? [...indices, response.choice] : indices
}

/**
 * How many respondents have answered each option index.
 *
 * The owner needs the count, not just the fact — `3 answers` on the authoring row and
 * `3 people have already answered` in the removal confirm both read from here, so the two
 * can never disagree. An index nobody has touched is absent rather than zero.
 */
export const getAnswerCounts = (responses: PollResponse[]): Map<number, number> => {
  const counts = new Map<number, number>()

  for (const response of responses) {
    for (const idx of getAnsweredIndices(response)) {
      counts.set(idx, (counts.get(idx) ?? 0) + 1)
    }
  }

  return counts
}

/**
 * Option indices any respondent has answered.
 *
 * Renaming one of these would silently change what somebody agreed to, so authoring is
 * blocked per option rather than for the whole poll.
 */
export const getAnsweredSlotIndices = (responses: PollResponse[]): Set<number> =>
  new Set(getAnswerCounts(responses).keys())

/**
 * `choices` is the binary projection every pre-existing consumer reads — `getProgress`,
 * `sortPollOptionsIndices`, the Final Option view. Availability writers derive it here so
 * the two can never drift.
 */
export const toChoices = (availability: Record<string, Availability>): Record<string, boolean> =>
  Object.fromEntries(Object.entries(availability).map(([idx, value]) => [idx, value === 'yes']))

const getAnswer = (response: PollResponse, idx: number): Availability | undefined =>
  response.availability?.[String(idx)]

export const getSlotTally = (responses: PollResponse[], idx: number): SlotTally =>
  responses.reduce<SlotTally>(
    (tally, response) => {
      const answer = getAnswer(response, idx)
      return answer ? { ...tally, [answer]: tally[answer] + 1 } : tally
    },
    { yes: 0, maybe: 0, no: 0 }
  )

/**
 * Sort by (yes desc, maybe desc), then earliest first.
 *
 * Never blend yes and maybe into a single score: a maybe is an unresolved answer, not a
 * half-attendance, and five maybes must not outrank four yeses. At equal support the earlier
 * night is the one a band wants offered first.
 */
export const rankDateSlots = (poll: Poll, responses: PollResponse[]) =>
  [...getLiveSlotIndices(poll)].sort((a, b) => {
    const left = getSlotTally(responses, a)
    const right = getSlotTally(responses, b)
    if (left.yes !== right.yes) {
      return right.yes - left.yes
    }
    if (left.maybe !== right.maybe) {
      return right.maybe - left.maybe
    }
    return (poll.pollOptions[a].start ?? '').localeCompare(poll.pollOptions[b].start ?? '')
  })

/** Slots every eligible member has said yes to — the ones that genuinely work for everyone. */
export const getViableSlotIndices = (poll: Poll, responses: PollResponse[], eligibleCount: number) =>
  getLiveSlotIndices(poll).filter(idx => eligibleCount > 0 && getSlotTally(responses, idx).yes === eligibleCount)

/**
 * Derived from the ACL-backed member list, not from `invited`. `invited` grants no access and
 * is empty for polls shared without the Invite action, so it is not the set of people who can
 * answer.
 *
 * A member is done when they have answered every CURRENT, non-past slot — the definition
 * auto-save needs, since there is no Save button to signal completion. Adding a slot therefore
 * returns everyone here, which is correct: a new question has been asked.
 */
export const getWaitingOn = (
  poll: Poll,
  memberIds: string[],
  /** Persisted, because `userId` — the thing that identifies who answered — only exists once saved. */
  responses: Persisted<PollResponse>[],
  now?: string
) => {
  const open = getLiveSlotIndices(poll).filter(idx => {
    const slot = poll.pollOptions[idx]
    return isDateSlot(slot) ? !isPastSlot(slot, now) : true
  })

  return memberIds.filter(userId => {
    const response = responses.find(candidate => candidate.userId === userId)
    return !response || open.some(idx => !response.availability?.[String(idx)])
  })
}

/** `a, b and c` — the way a person would read a list aloud. */
const joinNames = (names: string[]) =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`

/** Above this many names the line stops being scannable and collapses to a count. */
const maxNamesShown = 4
const namesBeforeCount = 3

/**
 * The `Waiting on` line, capped.
 *
 * Up to four names are listed in full; five or more collapse to three names and a count. The
 * cutoff is four rather than three deliberately: `Ada, Bo, Cy and 1 other` is worse than
 * naming the fourth person, so abbreviating can never save just one word.
 *
 * `full` always carries every name — a screen-reader user must not get the truncated version.
 * Overflow already has a destination: the Responses layout shows per-respondent state.
 *
 * Names are sorted so the line does not reshuffle between renders, which reads as churn.
 */
export const formatWaitingOn = (names: string[]) => {
  if (names.length === 0) {
    return undefined
  }

  const sorted = [...names].sort((a, b) => a.localeCompare(b))
  const full = `Waiting on ${joinNames(sorted)}`

  if (sorted.length <= maxNamesShown) {
    return { visible: full, full }
  }

  const remaining = sorted.length - namesBeforeCount
  const shown = sorted.slice(0, namesBeforeCount).join(', ')

  return {
    visible: `Waiting on ${shown} and ${remaining} ${remaining === 1 ? 'other' : 'others'}`,
    full,
  }
}
